import { Injectable } from "@angular/core";
import { ProductVendor } from "@app/main/components/videos/enums/CameraProductType";
import { IAccountGroupConfigDto } from "@app/main/modules/foundation/_models/_camera-configs/CameraConfigModels";
import { PeripheralService } from "@app/main/modules/foundation/_services/peripheral.service";
import { DataCacheService } from "@app/services/caching/data-cache.service";
import { StreamaxCameraParametersService } from "@app/services/camera/streamax-camera-parameters.service";
import { untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, Observable } from "rxjs";
import { map } from "rxjs/operators";

@Injectable({
    providedIn: "root"
})
export class ConfigAccountGroupService {

    private configAccountGroupCacheName: string = "configAccountGroupCache";
    constructor(
        private readonly dataCacheService: DataCacheService,
        private readonly peripheralService: PeripheralService,
        private readonly streamaxCameraParametersService: StreamaxCameraParametersService
    ) { }

    getConfigAccountGroups(invalidateCache: boolean = false): Observable<IAccountGroupConfigDto[]> {
        return this.dataCacheService.getOrRequest(this.configAccountGroupCacheName, () => this.createNewConfigAccountGroups(), { invalidateCache, keyName: "AccountId" });
    }
    
    private createNewConfigAccountGroups(): Observable<IAccountGroupConfigDto[]> {
        return combineLatest([
            this.streamaxCameraParametersService.getLatestParameterRecords(),
            this.peripheralService.getAllPeripheralsWithMappings()
        ]).pipe(
            untilDestroyed(this),
            map(([latestRecords, peripherals]) => {
                const usableLatest = latestRecords.filter(r => !!r.CameraDeviceParameterSettings.ProfileExceptionErrors);
                const streamaxPeripherals = peripherals.filter(p => p.Product.GroupId === ProductVendor.Streamax);

                const accountPeripheralGroups = this.groupByAccountId(streamaxPeripherals, 'vehicleMapping.vehicle.AccountId');
                const accountRecordGroups = this.groupByAccountId(usableLatest, 'AccountId');

                // Map peripherals to records for each account
                return Object.keys(accountPeripheralGroups).map(accountId => {
                    const peripheralWithConfigRecord = accountPeripheralGroups[accountId].map(peripheral => {
                        const latestRecord = accountRecordGroups[accountId]?.find(
                            record => record.Serial === peripheral.Serial
                        ) || null;

                        return {
                            Id: peripheral.Id,
                            Peripherals: peripheral,
                            LatestRecord: latestRecord
                        };
                    });

                    // Calculate combined errors for the account
                    const combinedErrors = peripheralWithConfigRecord.reduce((acc, item) => {
                        return acc + (item.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors?.Errors?.length || 0);
                    }, 0);

                    return {
                        Id: parseInt(accountId, 10),
                        AccountId: parseInt(accountId, 10),
                        PeripheralWithConfigRecord: peripheralWithConfigRecord,
                        CombinedErrors: combinedErrors
                    };
                });
            })
        );
    }

    private groupByAccountId<T>(items: T[], key: string): Record<string, T[]> {
        return items.reduce((groups, item) => {
            const accountId = key.split('.').reduce((o, k) => o?.[k], item);
            if (accountId) {
                if (!groups[accountId]) groups[accountId] = [];
                groups[accountId].push(item);
            }
            return groups;
        }, {} as Record<string, T[]>);
    }
}

